import * as THREE from 'three/build/three.module';
import { Vector3 } from 'three';
import { TWEEN } from 'three/examples/jsm/libs/tween.module.min';
import { J3CameraUtils } from './J3CameraUtils';
import { J3TweenHelper } from './J3TweenHelper';
import { EaseBase, EaseEnd, DomeParam, InternalAction } from './J3TypeList';

export class J3DomeUtils extends EventTarget {
    top: THREE.Object3D;
    markerLocked = false;   //マーカー上にマウスカーソルがある場合 true
    lockedMarker = '';
    tagLocked = false;
    lockedTag = '';     //現在ロック中のタグ
    useDome = false;
    domeRotOffset = [0, 90, 0];  //360°画像配置のオフセット角度
    //targetElem; //ターゲット要素
    //currentElem;
    //targetDome: THREE.Mesh = null;  //ターゲットのドーム
    //currentDome: THREE.Mesh = null; //現在のドーム
    elems = [];     //ドームオブジェクトの格納配列
    filterMarkers = false;  //移動アイコンを距離でスクリーニングするか？
    goalPosition = new Vector3(0, 0, 0);    //目標位置
    startPosition = new Vector3(0, 0, 0);   //移動開始時カメラ位置
    targetDistance = 1.0;  //MoveTo（カメラ移動時）の移動距離

    tweens = [];
    isTweening = false;
    duration = 1000;
    easeType = TWEEN.Easing.Quartic.InOut;
    easeType2 = TWEEN.Easing.Quartic.Out;
    easeType3 = TWEEN.Easing.Quartic.Out;

    //easeType2 = TWEEN.Easing.Sinusoidal.Out;
    spherePrefix = 'p';
    domeLength = 0; // ドーム画像の数
    importedDomes = 0;  //インポートされたドーム画像の全数
    importedBatch = 0;  //１回のバッチ処理で読み込まれた画像数
    batchNum = 20;  //１回のバッチ処理数
    importComplete: boolean = false; //画像読み込み完了？
    startIndex = 0;
    markerMaterial: THREE.MeshBasicMaterial = null;
    markerGeometry: THREE.PlaneGeometry = null;
    marker: THREE.Mesh;    //マーカーのメッシュ
    guide: THREE.Mesh;      //位置ガイド（デバッグ用）
    //sphereGeometry: THREE.SphereGeometry = null;   //ドームのGeometry
    pDomes; //ドーム関連のJSONパラメータ
    degToRad = Math.PI / 180.0;
    connectedDomes: DomeParam[] = [];   //現在ロードしているドーム
    currentDome: DomeParam = null; //移動前の現在のドーム
    targetDome: DomeParam = null;
    initialConnectsLength: number;
    skipModels: boolean = false;

    constructor(public cameraUtils: J3CameraUtils, public envImageDir = 'images', public markerPrefix = 'm', public faceToCamera = false) {
        // envImageDir: 360°画像の保存フォルダ
        // makerPrefix:　マーカーの接頭辞
        // tagPrefix: タグの接頭辞
        super();
    }

    Update() {
        if (this.useDome) {
            //初期画像読み込み
            if (!this.importComplete) { //初期状態の読み込みが完了していない
                //console.log('MakeDomes', this.importedBatch, '/', this.batchNum, ' & ', this.importedDomes, '/', this.pDomes.spheres.length);
                //if (this.importedDomes == this.pDomes.initialConnects.length) {
                if (this.importedDomes == this.initialConnectsLength) {
                    this.ShowConnectedMarkers();    //接続先ドームのマーカーを表示
                    this.importComplete = true; //初期画像読み込み完了
                    console.log('Completed', this.importedDomes);
                    const event = new CustomEvent("complete", { detail: { number: this.importedDomes } });
                    this.dispatchEvent(event);
                }
            }

            //ターゲットに近づける
            const relativePos = new Vector3();
            relativePos.subVectors(this.goalPosition, this.cameraUtils.controls.target);
            const distance = relativePos.length(); //目標点までの距離距離
            const distanceRatio = distance / this.targetDistance; //目標前の距離を当初距離でノーマライズしたもの

            //マーカーをカメラに対向させる
            this.FaceMarkerToCamera(true);

            if (this.isTweening) {
                //360°画像のクロスフェードを実現
                let ratio = THREE.MathUtils.clamp(distanceRatio, 0.0, 1.0);
                if (this.currentDome != null) this.currentDome.material.opacity = ratio;
                if (this.targetDome != null) this.targetDome.material.opacity = 1.0 - ratio;

                this.cameraUtils.LookAt(this.goalPosition);
                //console.log(this.currentDome.name, ratio);
            }
            //console.log(this.camera.position, this.controls.target);
            TWEEN.update();
        }
    }


    FaceMarkerToCamera(forced = false) {
        //マーカーをカメラに対向させる
        if (forced || this.faceToCamera) {
            //console.log('facing marker');
            for (const elem of this.connectedDomes) {
                //console.log(dome.marker.rotation);
                elem.marker.rotation.setFromRotationMatrix(this.cameraUtils.camera.matrix);
            }
        }
    }

    MoveCamera(targetPos: Vector3) {
        console.log('MoveCamera', targetPos);
        this.cameraUtils.camera.position.set(targetPos);    //ターゲットに近づける
        this.cameraUtils.controls.target.addScaledVector(targetPos);    //ターゲットに近づける
    }

    Init(top: THREE.Object3D, pDomes, _skipModels = false, widthSegments = 4, heightSegments = 4) {
        this.top = top;
        this.spherePrefix = pDomes.spherePrefix;    //ドーム画像名の接頭語
        this.domeLength = pDomes.spheres.length;
        this.pDomes = pDomes;
        this.skipModels = _skipModels;

        //共通マーカー画像をロード
        const pMarker = pDomes.marker;
        this.markerPrefix = pMarker.prefix;
        this.markerGeometry = new THREE.PlaneGeometry(1, 1, widthSegments, heightSegments);
        var textureLoader = new THREE.TextureLoader();
        textureLoader.load(pMarker.imagedir + pMarker.image, (texture) => {
            texture.encoding = THREE.sRGBEncoding;
            this.markerMaterial = new THREE.MeshBasicMaterial({
                map: texture,
                color: pMarker.color,
                depthTest: true,
                depthWrite: true,
                alphaTest: 0.1,
                transparent: true,
                opacity: pMarker.opacity,
                //colorWrite:false,
            });
            this.marker = new THREE.Mesh(this.markerGeometry, this.markerMaterial);    //マーカーオブジェクト
            //let connects = [];
            //for (let elem of this.pDomes.initialConnects) {
            //    connects.push(elem.no); //初期接続ドームの配列を作る
            //}
            //this.CreateDomes('none', connects, markerDraw);   //初期ドームの作成 3D俯瞰状態は現在のドームは'none'
            const markerDraw = false;
            this.initialConnectsLength = this.pDomes.initialConnects.length;
            this.CreateDomes('none', this.pDomes.initialConnects, markerDraw); //初期ドームの作成 3D俯瞰状態は現在のドームは'none'
        })

        //360°画像の調整（角度合わせ用）
        if (this.pDomes.debug.rotateByKey != undefined && this.pDomes.debug.rotateByKey == true) {
            document.addEventListener('keydown', (e: Event) => this.KeyDownMethod(e));
        }

        //ガイド画像
        if(pDomes.debug.guide.enable){
            var guideGeometry = new THREE.PlaneGeometry(1, 1, widthSegments, heightSegments);
            var textureLoader2 = new THREE.TextureLoader();
            textureLoader2.load(pDomes.debug.guide.imagedir + pDomes.debug.guide.image, (texture) => {
                texture.encoding = THREE.sRGBEncoding;
                var guideMaterial = new THREE.MeshBasicMaterial({
                    map: texture,
                    color: 0xFFFFFF,
                    depthTest: true,
                    depthWrite: true,
                    alphaTest: 0.1,
                    transparent: true,
                    opacity: 1.0,
                });
                this.guide = new THREE.Mesh(guideGeometry, guideMaterial);    //ガイドオブジェクト
                const scale = pDomes.debug.guide.scale;
                this.guide.scale.set(scale, scale, scale);  //ガイドのスケールを設定
                const rot = pDomes.debug.guide.rotation;
                this.guide.rotation.set(rot[0]*this.degToRad, rot[1]*this.degToRad, rot[2]*this.degToRad);  //ガイドの回転角を設定
                this.top.add(this.guide);
            })
        }
    }

    //キーダウン時のメソッド
    KeyDownMethod(evt) {
        this.Rotate360Image(evt); //画像の回転
    }

    Rotate360Image(evt) {
        var key = evt.key;
        var shift = evt.shiftKey;   //evt.shift:boolean
        var option = evt.ctrlKey;     //evt.ctrl:boolean
        //var alt = evt.altKey;     //command or Windows key:boolean
        //console.log('Rotate360Image ', key, shift, option);

        if (shift) {
            let delta: number = option ? this.pDomes.debug.altInc : this.pDomes.debug.normalInc;

            let rot = this.currentDome.dome.rotation;
            if (key == 'ArrowLeft') {
                //this.currentDome.dome.rotateOnAxis(axis, delta*this.degToRad); //rotateを使うとなぜか符号が反転する？
                this.currentDome.dome.rotation.set(rot.x, rot.y + delta * this.degToRad, rot.z);
            } else if (key == 'ArrowRight') {
                //this.currentDome.dome.rotateOnAxis(axis, -delta*this.degToRad);
                this.currentDome.dome.rotation.set(rot.x, rot.y - delta * this.degToRad, rot.z);
            }
            rot = this.currentDome.dome.rotation;
            console.log('Rotate', this.currentDome.dome.name, '[', (rot.x / this.degToRad - this.domeRotOffset[0]).toFixed(1), ',', (rot.y / this.degToRad - this.domeRotOffset[1]).toFixed(1), ',', (rot.z / this.degToRad - this.domeRotOffset[2]).toFixed(1), ']');
        }
    }


    RemoveAndDispose(obj: THREE.Object3D, dispose = false, geometry: THREE.Mesh = null, material: THREE.Object3D = null, texture: THREE.texture = null) {
        //console.log('RemoveAndDispose', obj.name);
        this.top.remove(obj);

        if (dispose) {
            //if(geometry) {
            geometry.dispose();
            //console.log('RemoveAndDispose texture', obj.name);
            //}

            //if(texture) {
            texture.dispose();

            //console.log('RemoveAndDispose texture', obj.name);
            //}
            //if(material) {
            material.dispose();
            //console.log('RemoveAndDispose material', obj.name);
            //}
        }

        const action: InternalAction = 'dispose';
        const event = new CustomEvent(action, { detail: { objectname: obj.name } });
        this.dispatchEvent(event);
    }

    DisposeConnectedDomes() {
        if (this.currentDome != null) {
            this.RemoveAndDispose(this.currentDome.dome, true, this.currentDome.geometry, this.currentDome.material, this.currentDome.texture);
        }
        for (const elem of this.connectedDomes) {
            //現在のドームでなければDispose
            if (elem.dome != this.targetDome.dome) {
                this.RemoveAndDispose(elem.dome, true, elem.geometry, elem.material, elem.texture);
            }
            this.RemoveAndDispose(elem.marker, false);
        }
        this.importedDomes = 0;
        this.connectedDomes = [];
    }

    CreateDomes(targetname = '', connects = [], markerDraw = true) {
        // コネクトドームの配列を作成
        let domes = [];  //作成するドーム名。現在のドーム名で初期化
        for (const num of connects) {
            const imagename = this.spherePrefix + num.toString().padStart(3, '0') + '.jpg';
            domes.push({ imagename: imagename, show: false });
        }

        for (const dome of domes) {
            for (const sphere of this.pDomes.spheres) {
                if (sphere.image == dome.imagename) {   //全Sphereリストから連結先のドームのみ処置する
                    if (dome.imagename != targetname) {
                        const forceRender = false;
                        const marker = new THREE.Mesh(this.markerGeometry, this.markerMaterial);
                        this.SetDome(this.top, marker, this.markerMaterial, sphere.image, this.pDomes.color, this.pDomes.sRGB, this.pDomes.baseRadius * sphere.radius, this.pDomes.baseRange * sphere.range, sphere.connect, sphere.position, sphere.rotation, dome.show, sphere.omit, sphere.visible, this.pDomes.marker, forceRender, this.pDomes.marker.heightoffset, this.pDomes.widthSegments, this.pDomes.heightSegments);
                        if (markerDraw) this.top.add(marker);
                    }
                    break;
                }
            }
        }
        for (const elem of this.connectedDomes) {
            console.log('CreateDomes', elem.dome.name);    //接続ドームの名前
        }
    }

    SetDome(top: THREE.Object3D, marker: THREE.Mesh, markerMaterial: THREE.Material, imagename: string, color = 0xffffff, sRGB = true, radius = 10.0, range = 40.0, connect = [], position = [0, 0, 0], rotation = [0, 0, 0], initial = false, omit = false, visible = true, pMarker, forceRender = false, heightoffset = 0, widthSegments = 32, heightSegments = 16) {
        // top トップオブジェクト
        // marker マーカーメッシュ
        // imagename 360°画像ファイル名
        // radius 球体半径 (m)
        // position 位置 (m)
        // rotation 回転角 (deg)
        // initial 初期位置か？
        // omit 作成除外(bool)？　通常はfalse
        // visible 表示（bool) 通常はtrue
        // pMarker マーカー関連のパラメータ

        //作成除外
        if (omit) {
            console.log('omitted', imagename);
            return;
        }

        const degToRad = this.degToRad;
        this.useDome = true;    //ドーム処理を有効にする Updateループ用フラグ
        /*
        if (this.sphereGeometry == null) {
            this.sphereGeometry = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
            this.sphereGeometry.scale(-1, 1, 1); //表裏反転
        }
        */
        const sphereGeometry = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
        //sphereGeometry.scale(-1, 1, 1); //表裏反転 

        marker.name = this.markerPrefix + '_' + imagename;  //マーカーの名前
        //console.log('marker.name', marker.name);
        marker.position.set(position[0], position[1] + pMarker.heightOffset, position[2]);
        const mrot = pMarker.rotation;
        marker.rotation.set(mrot[0] * degToRad, mrot[1] * degToRad, mrot[2] * degToRad);
        marker.scale.set(2 * pMarker.size, 2 * pMarker.size, 1);
        //marker.visible = visible;
        marker.visible = false; //いったん非表示にする。Update()ループ内ですべてのテクスチャを読み込んだことを確認した後表示する
        marker.renderOrder = 1; //背景Sphereよりあとでレンダリングするように
        
        // 360°画像の読み込み
        var textureLoader = new THREE.TextureLoader();
        textureLoader.load(this.envImageDir + imagename, (texture) => {
            if (sRGB) {
                texture.encoding = THREE.sRGBEncoding;
            } else {
                texture.encoding = THREE.LinearEncoding;
            }
            const material = new THREE.MeshBasicMaterial({
                //const material = new THREE.MeshMatcapMaterial( {
                map: texture,
                //side:THREE.BackSide,
                color: color,
                depthTest: true,
                depthWrite: false,
                transparent: true,
            });
            const sphere = new THREE.Mesh(sphereGeometry, material);
            sphere.position.set(position[0], position[1] + heightoffset, position[2]);
            sphere.rotation.set((rotation[0] + this.domeRotOffset[0]) * degToRad,
                (rotation[1] + this.domeRotOffset[1]) * degToRad,
                (rotation[2] + this.domeRotOffset[2]) * degToRad);
            sphere.name = imagename;
            //console.log(sphere.name, sphere.rotation.x, sphere.rotation.y, sphere.rotation.z);
            //反転
            const flipNormals = false;
            if (!flipNormals) {
                sphereGeometry.scale(-1, 1, 1); //表裏反転 
                //sphere.geometry.computeVertexNormals();
                //sphere.geometry.computeFaceNormals();
            } else {
                //法線ベクトルを反転する方法。が、こちらの方法はエラーになる
                sphereGeometry.scale(-1, 1, 1); //表裏反転 
                sphere.geometry.dynamic = true
                sphere.geometry.__dirtyVertices = true;
                sphere.geometry.__dirtyNormals = true;

                sphere.flipSided = true;

                //flip every vertex normal in mesh by multiplying normal by -1
                for (var i = 0; i < sphere.geometry.faces.length; i++) {
                    sphere.geometry.faces[i].normal.x = -1 * sphere.geometry.faces[i].normal.x;
                    sphere.geometry.faces[i].normal.y = -1 * sphere.geometry.faces[i].normal.y;
                    sphere.geometry.faces[i].normal.z = -1 * sphere.geometry.faces[i].normal.z;
                }
                sphere.geometry.computeVertexNormals();
                sphere.geometry.computeFaceNormals();
            }

            if (initial) {
                //初期表示ドーム
                this.targetDome = sphere;
                this.currentDome = sphere;
                sphere.visible = visible;
                marker.visible = false;
                console.log('created initial', imagename, initial, visible);
            } else {
                //marker.visible = visible;   //マーカーを表示する→Update()ループに移動
                if (forceRender) {
                    sphere.visible = true;  //強制的に描画（デバッグ用）
                } else {
                    sphere.visible = false;  //通常初期表示以外は非表示
                }

                const distance = this.GetDistanceVectors(sphere.position, this.goalPosition);
                if (distance < range) {
                    marker.visible = visible;  //visible
                } else {
                    marker.visible = false; //false
                }
                //console.log('created', imagename, initial, visible, distance);
            }

            //top.add(sphere);   //360°画像をシーンに追加

            this.connectedDomes.push({ dome: sphere, marker: marker, range: range, connect: connect, markermaterial: markerMaterial, geometry: sphereGeometry, material: material, texture: texture, imagename: imagename, position: position, rotation: rotation });
            this.importedDomes++;
            //console.log('Dome imported', imagename, this.importedDomes, sphere.name, marker.name);
            const event = new CustomEvent("imported", { detail: { number: this.importedDomes } });
            this.dispatchEvent(event);
        });
    }

    ShowConnectedMarkers() {
        //現在の接続ドームのマーカーを表示する
        for (const dome of this.connectedDomes) {
            dome.marker.visible = true;
        }
    }

    GetDistanceVectors(vec1: Vector3, vec2: Vector3): number {
        const relativePos = new Vector3();
        relativePos.subVectors(vec1, vec2);
        return relativePos.length(); //目標点までの距離距離
    }


    SetMoveParams(duration = 1000, easeBase: EaseBase = 'Quartic', easeEnd: EaseEnd = 'InOut') {
        this.duration = duration;
        const tweenHelper = new J3TweenHelper();
        const easeType = tweenHelper.GetEaseType(easeBase, easeEnd);

        this.easeType = easeType;
    }


    JumpTo(imagename: string, forceCamPosition: boolean = false, offset: number[] = [0, 0, 1], forceLevel: boolean = false) {

    }


    MoveTo(markername: string, forceCamPosition: boolean = false, offset: number[] = [0, 0, 1], forceLevel: boolean = false) {
        //
        const imagename = markername.split('_')[1];  //接頭辞'mk_'を取り除く＝画像ファイル名
        /*
        let targetMarker: THREE.Mesh;
        for (const elem of this.connectedDomes) {
            if (elem.marker.name == markername) {
                targetMarker = elem.marker;
                //const factor = 0.7;
                //console.log('marker animation', targetMarker.name, targetMarker.scale, factor);
                //マーカーの消去アニメーション
                // ここにマテリアルのアルファを変更するアニメーションを入れる
                break;
            }
        }
        */
        for (const elem of this.connectedDomes) {
            if (elem.dome.name == imagename) {
                if (this.targetDome != null && this.targetDome.dome == elem.dome) {
                    console.log('Move Canceled', elem.dome.name);
                    return;
                }
                this.targetDome = elem;
                elem.dome.visible = true;       //360°表示
                elem.marker.visible = true;
                //console.log('MoveTo', elem.dome.name, elem.dome.visible, elem.dome.position, elem.dome.scale);
                console.log('MoveTo', elem.dome.name);
                this.top.add(elem.dome);

                const pos = elem.dome.position;
                this.goalPosition = pos;
                this.cameraUtils.LookAt(this.goalPosition);

                if (forceLevel) {
                    //強制的に水平位置に
                    const currentPos = this.cameraUtils.controls.target;
                    this.cameraUtils.controls.target.set(currentPos.x, currentPos.y, this.goalPosition.z);
                }
                this.startPosition = this.cameraUtils.controls.target;  //現在位置
                const relativePos = new Vector3();
                relativePos.subVectors(this.goalPosition, this.startPosition);
                this.targetDistance = relativePos.length();    //移動目標距離
                //this.targetDome.dome = elem.dome; //移動目標のドームGeometry
                this.targetDome = elem;

                //カメラ位置はRender()内でトゥイーンする
                const removeAll = true;
                if (removeAll) {
                    TWEEN.removeAll();
                    this.tweens = [];
                }

                let rel = new Vector3();
                if (forceCamPosition) {
                    //カメラの方向を強制的に指定（ランディング時に使用）
                    rel = new Vector3(offset[0], offset[1], offset[2]);
                    //console.log('offset', offset, 'rel', rel);
                    //console.log('forceCamPosition', pos.x - rel.x, pos.y - rel.y, pos.z - rel.z);
                    //this.cameraUtils.camera.position.set(pos.x - rel.x, pos.y - rel.y, pos.z - rel.z );
                    const factor = 0.9;
                    new TWEEN.Tween(this.cameraUtils.camera.position)
                        .to({ x: pos.x - rel.x, y: pos.y - rel.y, z: pos.z - rel.z }, this.duration * factor)
                        //.to({ x: pos.x+1, y: pos.y, z: pos.z  }, this.duration)
                        .easing(this.easeType2)
                        .start();
                } else {
                    //カメラの方向は変えない継承（通常の移動）
                    rel.subVectors(this.cameraUtils.controls.target, this.cameraUtils.camera.position); //コントロールターゲットとカメラの相対距離

                    //カメラのTween
                    //console.log('target', this.cameraUtils.controls.target, 'camera', this.cameraUtils.camera.position, 'rel', rel);
                    new TWEEN.Tween(this.cameraUtils.camera.position)
                        .to({ x: pos.x - rel.x, y: pos.y - rel.y, z: pos.z - rel.z }, this.duration)
                        //.to({ x: pos.x+1, y: pos.y, z: pos.z  }, this.duration)
                        .easing(this.easeType)
                        .start();

                    //目標タグのTween
                }
                //カメラコントロールのTween .onComplete()で移動後の処置を行っている
                new TWEEN.Tween(this.cameraUtils.controls.target)
                    .to({ x: this.goalPosition.x, y: this.goalPosition.y, z: this.goalPosition.z }, this.duration)
                    .easing(this.easeType)
                    .onStart(() => { this.isTweening = true; })
                    .onComplete(() => {
                        this.isTweening = false;
                        //console.log('after move orbit target', this.cameraUtils.controls.target, 'camera.position after', this.cameraUtils.camera.position);
                        //遠方マーカーを非表示
                        //現在の連結ドームを廃棄
                        this.DisposeConnectedDomes();
                        this.currentDome = this.targetDome;
                        this.currentDome.material.opacity = 1.0;
                        //this.FilterMarkersConnected();    //マーカーは都度Disposeするようにしたので不要となった
                        this.CreateDomes(imagename, elem.connect);  //連結ドームを作成する
                        console.log('Moved to', this.currentDome.dome.name);
                        const event = new CustomEvent("moved", { detail: { domeName: this.currentDome.dome.name } });
                        this.dispatchEvent(event);
                        //ガイド画像
                        if(this.pDomes.debug.guide.enable){
                            const ofset = this.pDomes.debug.guide.position;
                            this.guide.position.set(this.goalPosition.x + ofset[0], this.goalPosition.y + ofset[1], this.goalPosition.z  + ofset[2]);
                        }
                    })
                    .start();

                break;
            }
        }
    }
    /*
        ReloadTextureAndAdd(elem) {
            if (!this.pDomes.dispose) {
                const degToRad = this.degToRad;
                var textureLoader = new THREE.TextureLoader();
                textureLoader.load(this.envImageDir + elem.imagename, (texture) => {
                    if (this.pDomes.sRGB) {
                        texture.encoding = THREE.sRGBEncoding;
                    } else {
                        texture.encoding = THREE.LinearEncoding;
                    }
                    const material = new THREE.MeshBasicMaterial({
                        //const material = new THREE.MeshMatcapMaterial( {
                        map: texture,
                        color: this.pDomes.color,
                        depthWrite: false,
                        transparent: true,
                    });
                    const sphere = new THREE.Mesh(this.sphereGeometry, material);
                    const position = elem.position;
                    sphere.position.set(position[0], position[1], position[2]);
                    const rotation = elem.rotation;
                    sphere.rotation.set((rotation[0] + this.domeRotOffset[0]) * degToRad,
                        (rotation[1] + this.domeRotOffset[1]) * degToRad,
                        (rotation[2] + this.domeRotOffset[2]) * degToRad);
                    sphere.name = elem.imagename;
                    sphere.visible = true;
                    //console.log(sphere.name, sphere.rotation.x, sphere.rotation.y, sphere.rotation.z);
                    this.top.add(sphere);
                    elem.material = material;
                    elem.texture = texture;
                    return elem;
                });
            }
        }
    */
    FilterMarkersByDistance() {
        //一定距離以上のマーカーを消去
        console.log('this.goalPosition', this.goalPosition);
        for (const elem of this.elems) {
            //console.log('elem.dome.position', elem.dome.position);
            if (elem.dome == this.currentDome) {
                elem.marker.visible = false;
            } else {
                const distance = this.GetDistanceVectors(this.goalPosition, elem.dome.position);
                if (distance > elem.range) {
                    elem.marker.visible = false;
                } else {
                    elem.marker.visible = true;
                }
            }
        }
    }


    FilterMarkersConnected() {
        //接続マーカーのみ表示
        const connect = this.currentDome.connect;    //現在のポイントの接続要素配列
        let connectNames: string[] = []; //接続インデックス配列から画像名配列を作成
        for (const num of connect) {
            const name = this.spherePrefix + num.toString().padStart(3, '0') + '.jpg';
            connectNames.push(name);
        }
        //console.log(connectNames);

        for (const elem of this.elems) {
            //elem.marker.visible = false;
            if (elem == this.currentDome) {
                elem.marker.visible = false;    //現在時点のマーカーは非表示
                elem.marker.scale.set(0, 0, 0);
                elem.dome.visible = true;
                this.top.remove(elem.marker);

            } else {
                for (const name of connectNames) {
                    if (elem.dome.name == name) {
                        elem.marker.visible = true; //接続先のマーカーは表示
                        const size = this.pDomes.marker.size;
                        elem.marker.scale.set(size, size, size);
                        this.top.add(elem.marker);
                        break;
                    } else {
                        elem.marker.visible = false;    //接続先以外のマーカーは非表示
                        elem.marker.scale.set(0, 0, 0);   //誤クリックをふせぐためスケールをゼロに
                        this.top.remove(elem.marker);
                    }

                }
            }
        }
    }
    ShowAllMarkers() {
        for (const elem of this.elems) {
            elem.marker.visible = true;    //俯瞰表示用
        }
    }

}